import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAdminTechnical } from "@/features/admin";
import { useState } from "react";
import { AdminPageLayout } from "../layout";
import { PeriodSelect } from "./components/period-select";
import { StatCard } from "./components/stat-card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const ms = (n: number) =>
  n >= 1000 ? `${(n / 1000).toFixed(2)} s` : `${Math.round(n)} ms`;
const pct = (n: number) => `${(n * 100).toFixed(1)} %`;

/** Colour a silence ratio: green under 20%, amber under 40%, red beyond. */
const silenceTone = (r: number): "default" | "warn" | "bad" =>
  r >= 0.4 ? "bad" : r >= 0.2 ? "warn" : "default";

/** Colour a response delay: green under 1.5s, amber under 3s, red beyond. */
const delayTone = (n: number): "default" | "warn" | "bad" =>
  n >= 3000 ? "bad" : n >= 1500 ? "warn" : "default";

/** Technical analysis of call quality across all tenants. */
export function AdminTechnicalPage() {
  const [days, setDays] = useState(30);
  const { data, isLoading } = useAdminTechnical(days);

  const silence = data?.silence_ratio;
  const delay = data?.response_delay_ms;
  const breakdown = data?.response_delay_breakdown;
  const fb = data?.fallback;
  const st = data?.starter;

  return (
    <AdminPageLayout
      title="Analyse technique"
      description={
        data
          ? `${data.sessions_analyzed} sessions analysées sur la période`
          : "Qualité technique des appels, tous tenants confondus"
      }
      actions={[<PeriodSelect days={days} onChange={setDays} />]}
    >
      {/* Silence ratio — higher is worse */}
      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-muted-foreground">
          Temps mort — délai de réponse cumulé / durée d'appel (plus c'est haut,
          moins c'est bon)
        </h3>
        <div className="grid grid-cols-3 gap-4">
          <StatCard
            title="p50"
            value={silence ? pct(silence.p50) : "—"}
            isLoading={isLoading}
            tone={silence ? silenceTone(silence.p50) : "default"}
          />
          <StatCard
            title="p75"
            value={silence ? pct(silence.p75) : "—"}
            isLoading={isLoading}
            tone={silence ? silenceTone(silence.p75) : "default"}
          />
          <StatCard
            title="p95"
            value={silence ? pct(silence.p95) : "—"}
            isLoading={isLoading}
            tone={silence ? silenceTone(silence.p95) : "default"}
          />
        </div>
      </section>

      {/* Response delay (time to first audio chunk) */}
      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-muted-foreground">
          Délai de réponse — temps jusqu'au premier chunk audio
        </h3>
        <div className="grid grid-cols-3 gap-4">
          <StatCard
            title="p50"
            value={delay ? ms(delay.p50) : "—"}
            isLoading={isLoading}
            tone={delay ? delayTone(delay.p50) : "default"}
          />
          <StatCard
            title="p75"
            value={delay ? ms(delay.p75) : "—"}
            isLoading={isLoading}
            tone={delay ? delayTone(delay.p75) : "default"}
          />
          <StatCard
            title="p95"
            value={delay ? ms(delay.p95) : "—"}
            isLoading={isLoading}
            tone={delay ? delayTone(delay.p95) : "default"}
          />
        </div>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-medium text-muted-foreground">
              Décomposition moyenne du délai
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <Stage
                label="Endpointing (VAD)"
                value={breakdown ? ms(breakdown.endpointing_ms) : "—"}
              />
              <Stage
                label="LLM — 1er token (étage, avec prep)"
                value={breakdown ? ms(breakdown.llm_first_token_ms) : "—"}
              />
              <Stage
                label="Audio — 1er chunk"
                value={breakdown ? ms(breakdown.audio_first_chunk_ms) : "—"}
              />
            </div>
          </CardContent>
        </Card>
      </section>

      {/* API latency by type / provider */}
      <section className="space-y-2">
        <p className="text-xs text-muted-foreground">
          Latence réelle du provider (réseau + modèle), hors prep interne — pour
          le LLM c'est le TTFT pur, à comparer à l'étage « LLM — 1er token »
          ci-dessus.
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <LatencyTable
            title="Délai des API critiques — par type"
            rows={(data?.api_latency_by_type || []).map((r) => ({
              label: r.type,
              avg_ms: r.avg_ms,
              count: r.count,
            }))}
          />
          <LatencyTable
            title="Délai des API critiques — par provider"
            rows={(data?.api_latency_by_provider || []).map((r) => ({
              label: `${r.provider} (${r.type})`,
              avg_ms: r.avg_ms,
              count: r.count,
            }))}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Le provider <code>bedrock</code> correspond au « starter » (préfixe
          rapide joué pendant que le modèle principal réfléchit) : comparer son
          TTFT à celui du modèle principal montre le temps mort qu'il fait
          gagner.
        </p>
      </section>

      {/* Token usage per model — surfaces the starter footprint next to the main model */}
      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-muted-foreground">
          Usage tokens par modèle
        </h3>
        <p className="text-xs text-muted-foreground">
          « Cache » = tokens de prompt servis depuis le cache du fournisseur,
          facturés une fraction du tarif d'entrée. Le taux se lit{" "}
          <strong>par ligne</strong> : chaque modèle a son propre cache, un
          total mélangé n'a pas de sens. Un taux proche de zéro sur des appels
          longs signifie que le préfixe est invalidé à chaque tour — on paie
          plein tarif un contenu qu'on renvoie à l'identique. Les lignes « hors
          appel » sont le travail fait après la conversation, sans personne en
          attente (clôture, revues FAQ / Messagerie, SMS) : facturé pareil, mais
          compté à part.
        </p>
        <ModelTokensTable rows={data?.tokens_by_model || []} />
      </section>

      {/* Starter (fast-model prefix) usage */}
      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-muted-foreground">
          Starter — préfixe rapide « withStarter » ({st?.attempted ?? 0} tours)
        </h3>
        <p className="text-xs text-muted-foreground">
          Le starter parle un préfixe court pendant que le modèle principal
          réfléchit — aujourd'hui le prédicteur de hooks local, autrefois un
          modèle rapide distant. « Refusé » regroupe toutes les raisons de ne
          pas l'avoir parlé (info chiffrée ou temporelle, budget dépassé,
          prédicteur indisponible, langue non couverte) ; le détail par tour est
          sur le message lui-même, en mode debug.
        </p>
        <div className="grid grid-cols-3 gap-4">
          <StatCard
            title="Tours avec starter"
            value={st?.attempted ?? 0}
            isLoading={isLoading}
          />
          <StatCard
            title="Préfixe utilisé (parlé)"
            value={st?.used ?? 0}
            hint={
              st?.attempted ? pct((st.used || 0) / st.attempted) : undefined
            }
            isLoading={isLoading}
          />
          <StatCard
            title="Préfixe refusé (toutes raisons)"
            value={st?.rejected ?? 0}
            hint={
              st?.attempted ? pct((st.rejected || 0) / st.attempted) : undefined
            }
            isLoading={isLoading}
            tone={st && st.rejected > 0 ? "warn" : "default"}
          />
        </div>
      </section>

      {/* Fallback usage */}
      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-muted-foreground">
          Passages en fallback ({fb?.total ?? 0} tours)
        </h3>
        <div className="grid grid-cols-4 gap-4">
          <StatCard
            title="Fallback non nécessaire"
            value={fb?.not_needed ?? 0}
            hint={fb?.total ? pct((fb.not_needed || 0) / fb.total) : undefined}
            isLoading={isLoading}
          />
          <StatCard
            title="Appelé mais non utilisé"
            value={fb?.called_unused ?? 0}
            hint={
              fb?.total ? pct((fb.called_unused || 0) / fb.total) : undefined
            }
            isLoading={isLoading}
            tone="warn"
          />
          <StatCard
            title="Appelé et utilisé"
            value={fb?.called_used ?? 0}
            hint={fb?.total ? pct((fb.called_used || 0) / fb.total) : undefined}
            isLoading={isLoading}
            tone="bad"
          />
          {/* The one to watch: the fail-over was called and DIED, so the turn ran with no
              safety net and no deadline. Any non-zero value here is a bug, not a load
              signal — unlike the three above, which are all normal operating states. */}
          <StatCard
            title="Appelé mais en échec"
            value={fb?.called_failed ?? 0}
            hint={
              fb?.total ? pct((fb.called_failed || 0) / fb.total) : undefined
            }
            isLoading={isLoading}
            tone="bad"
          />
        </div>
      </section>
    </AdminPageLayout>
  );
}

function ModelTokensTable({
  rows,
}: {
  rows: {
    scope?: "live" | "background";
    provider: string;
    model: string;
    prompt: number;
    completion: number;
    total: number;
    cached_prompt: number;
    calls: number;
  }[];
}) {
  const num = (n: number) => n.toLocaleString("fr-FR");
  // Share of prompt tokens that came from the cache. Undefined rather than 0% when the
  // model sent no prompt tokens at all, so "no data" never reads as "cache broken".
  const hitRate = (row: { prompt: number; cached_prompt: number }) =>
    row.prompt > 0 ? row.cached_prompt / row.prompt : undefined;
  return (
    <Card>
      <CardContent className="pt-6">
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucune donnée.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Modèle</TableHead>
                <TableHead className="text-right">Appels</TableHead>
                <TableHead className="text-right">Prompt</TableHead>
                <TableHead className="text-right">Cache</TableHead>
                <TableHead className="text-right">Complétion</TableHead>
                <TableHead className="text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow
                  key={`${r.scope ?? "live"}:${r.provider}:${r.model}`}
                >
                  <TableCell>
                    <span className="font-medium">{r.provider}</span>
                    <span className="text-muted-foreground"> · {r.model}</span>
                    {r.scope === "background" && (
                      <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                        hors appel
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {num(r.calls)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {num(r.prompt)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {hitRate(r) === undefined ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <>
                        <span
                          className={
                            hitRate(r)! < 0.2 ? "text-red-600 font-medium" : ""
                          }
                        >
                          {pct(hitRate(r)!)}
                        </span>
                        <span className="text-muted-foreground">
                          {" "}
                          ({num(r.cached_prompt)})
                        </span>
                      </>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {num(r.completion)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {num(r.total)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function Stage({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-lg font-semibold tabular-nums">{value}</span>
    </div>
  );
}

function LatencyTable({
  title,
  rows,
}: {
  title: string;
  rows: { label: string; avg_ms: number; count: number }[];
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucune donnée.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Délai moyen</TableHead>
                <TableHead className="text-right">Mesures</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.label}>
                  <TableCell>{r.label}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {ms(r.avg_ms)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {r.count}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
